import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { 
  Cloud, 
  CheckCircle2, 
  ArrowRight, 
  ArrowLeft,
  Shield,
  Zap,
  BarChart3,
  Bell,
  Sparkles,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const QuickStart = ({ onComplete, onSkip }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    cloudProvider: '',
    accountName: '',
    budgetLimit: '',
    alertEmail: '',
    alertThreshold: '80'
  });

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  const cloudProviders = [
    { id: 'aws', name: 'Amazon Web Services', icon: '🟠', color: 'bg-orange-500' },
    { id: 'azure', name: 'Microsoft Azure', icon: '🔵', color: 'bg-blue-500' },
    { id: 'gcp', name: 'Google Cloud Platform', icon: '🔴', color: 'bg-red-500' },
    { id: 'snowflake', name: 'Snowflake', icon: '❄️', color: 'bg-cyan-500' }
  ];

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // Simulate setup completion
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Store quick start completion in localStorage
      localStorage.setItem('cartie_quickstart_complete', 'true');
      localStorage.setItem('cartie_quickstart_data', JSON.stringify(formData));
      
      toast.success('Setup complete! Welcome to CARTIEAI.');
      
      if (onComplete) {
        onComplete(formData);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('cartie_quickstart_skipped', 'true');
    if (onSkip) {
      onSkip();
    } else {
      navigate('/dashboard');
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6" data-testid="quickstart-step-1">
            <div className="text-center space-y-2">
              <div className="w-16 h-16 mx-auto bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold">Welcome to CARTIEAI</h2>
              <p className="text-slate-400 dark:text-slate-400">
                Let's get you set up in under 2 minutes
              </p>
            </div>

            <div className="grid gap-4">
              {[
                { icon: BarChart3, title: 'Real-time Cost Visibility', desc: 'See where every dollar goes' },
                { icon: Zap, title: 'AI-Powered Insights', desc: 'Get intelligent recommendations' },
                { icon: Bell, title: 'Proactive Alerts', desc: 'Never exceed your budget' },
                { icon: Shield, title: 'Budget Guardrails', desc: 'Automatic cost protection' }
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                  <feature.icon className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="font-medium text-sm">{feature.title}</p>
                    <p className="text-xs text-slate-400">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6" data-testid="quickstart-step-2">
            <div className="text-center space-y-2">
              <div className="w-16 h-16 mx-auto bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <Cloud className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold">Select Cloud Provider</h2>
              <p className="text-slate-400 dark:text-slate-400">
                Which cloud platform do you use most?
              </p>
            </div>

            <div className="grid gap-3">
              {cloudProviders.map((provider) => (
                <button
                  key={provider.id}
                  onClick={() => setFormData({ ...formData, cloudProvider: provider.id })}
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                    formData.cloudProvider === provider.id
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                  }`}
                  data-testid={`provider-${provider.id}`}
                >
                  <span className="text-2xl">{provider.icon}</span>
                  <span className="font-medium">{provider.name}</span>
                  {formData.cloudProvider === provider.id && (
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 ml-auto" />
                  )}
                </button>
              ))}
            </div>

            <div className="space-y-2">
              <Label htmlFor="accountName">Account Name (Optional)</Label>
              <Input
                id="accountName"
                placeholder="e.g., Production, Development"
                value={formData.accountName}
                onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
                data-testid="account-name-input"
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6" data-testid="quickstart-step-3">
            <div className="text-center space-y-2">
              <div className="w-16 h-16 mx-auto bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                <Shield className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold">Set Budget Guardrail</h2>
              <p className="text-slate-400 dark:text-slate-400">
                We'll alert you before you exceed your budget
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="budgetLimit">Monthly Budget Limit ($)</Label>
                <Input
                  id="budgetLimit"
                  type="number"
                  placeholder="e.g., 10000"
                  value={formData.budgetLimit}
                  onChange={(e) => setFormData({ ...formData, budgetLimit: e.target.value })}
                  data-testid="budget-limit-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="alertThreshold">Alert at % of budget</Label>
                <div className="flex items-center gap-4">
                  <Input
                    id="alertThreshold"
                    type="range"
                    min="50"
                    max="95"
                    step="5"
                    value={formData.alertThreshold}
                    onChange={(e) => setFormData({ ...formData, alertThreshold: e.target.value })}
                    className="flex-1"
                    data-testid="alert-threshold-input"
                  />
                  <span className="font-mono text-lg font-bold text-emerald-600 w-12">
                    {formData.alertThreshold}%
                  </span>
                </div>
                <p className="text-xs text-slate-400">
                  You'll be notified when spending reaches {formData.alertThreshold}% of ${formData.budgetLimit || '0'}
                </p>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Pro tip:</strong> Start with 80% threshold. This gives you time to react before hitting your limit.
              </p>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6" data-testid="quickstart-step-4">
            <div className="text-center space-y-2">
              <div className="w-16 h-16 mx-auto bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold">You're All Set!</h2>
              <p className="text-slate-400 dark:text-slate-400">
                Review your setup and start saving
              </p>
            </div>

            <div className="space-y-3">
              <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Cloud Provider</span>
                  <span className="font-medium">
                    {cloudProviders.find(p => p.id === formData.cloudProvider)?.name || 'Not selected'}
                  </span>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Account Name</span>
                  <span className="font-medium">{formData.accountName || 'Default'}</span>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Monthly Budget</span>
                  <span className="font-medium">${formData.budgetLimit || '0'}</span>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Alert Threshold</span>
                  <span className="font-medium">{formData.alertThreshold}%</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="alertEmail">Alert Email (Optional)</Label>
              <Input
                id="alertEmail"
                type="email"
                placeholder="you@company.com"
                value={formData.alertEmail}
                onChange={(e) => setFormData({ ...formData, alertEmail: e.target.value })}
                data-testid="alert-email-input"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="quickstart-modal">
      <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="relative">
          <button
            onClick={handleSkip}
            className="absolute right-4 top-4 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            data-testid="quickstart-skip-btn"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <span>Step {step} of {totalSteps}</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {renderStep()}

          <div className="flex items-center justify-between pt-4 border-t">
            {step > 1 ? (
              <Button variant="ghost" onClick={handleBack} data-testid="quickstart-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            ) : (
              <Button variant="ghost" onClick={handleSkip} data-testid="quickstart-skip-text-btn">
                Skip for now
              </Button>
            )}

            {step < totalSteps ? (
              <Button 
                onClick={handleNext} 
                disabled={step === 2 && !formData.cloudProvider}
                data-testid="quickstart-next-btn"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleComplete} 
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="quickstart-complete-btn"
              >
                {loading ? 'Setting up...' : 'Start Using CARTIEAI'}
                <Sparkles className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuickStart;
